import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  RefreshCw,
  Package, 
  DollarSign, 
  Truck, 
  AlertTriangle,
  Calendar,
  User,
  Tag,
  Layers,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowDownCircle,
  ArrowUpCircle,
  MinusCircle,
  ExternalLink,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent, Skeleton } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Tabs, TabItem } from '@components/Tabs';
import { Modal } from '@components/Dialog';
import { LabelQuantityDialog } from '@components/LabelQuantityDialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { PendingOrdersModal } from '@components/PendingOrdersModal';
import { productService } from '@services/productService';
import { productCategoryService } from '@services/productCategoryService';
import { productClassificationService } from '@services/productClassificationService';
import { stockMovementService } from '@services/stockMovementService';
import { purchaseOrderService } from '@services/purchaseOrderService';
import { materialReceiptService } from '@services/materialReceiptService';
import { salesOrderService } from '@services/salesOrderService';
import { Product, CostLayer, PendingOrderInfo, ProductCategory } from '@/types/product';
import { ProductType, ProductOrigin } from '@/types/productClassification';
import { PurchaseOrder } from '@/types/purchaseOrder';
import { MaterialReceipt } from '@/types/materialReceipt';
import { SalesOrder } from '@/types/salesOrder';
import {
  StockMovement,
  MovementType,
  OriginType,
} from '@/types/stockMovement';
import { clsx } from 'clsx';

/**
 * Página de detalhes do produto
 * Exibe todas as informações do produto em formato somente leitura
 */
/**
 * Thumb da foto do projeto (produto personalizado). O container do blob é
 * privado: busca o binário pelo proxy e exibe via object URL; clique amplia.
 */
const ProjectPhotoThumb: React.FC<{ productId: number }> = ({ productId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    productService
      .getProjectPhotoFile(productId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => {
        // Sem foto ou erro: não renderiza nada.
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productId]);

  if (!url) return null;

  return (
    <>
      <button type="button" onClick={() => setExpanded(true)} title="Ampliar foto do projeto">
        <img src={url} alt="Foto do projeto" className="w-24 h-24 object-cover rounded-lg border border-slate-200 hover:opacity-80" />
      </button>
      {expanded && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setExpanded(false)}
        >
          <img src={url} alt="Foto do projeto" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
};

export const ProductDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);

  // Cost layers state
  const [costLayers, setCostLayers] = useState<CostLayer[]>([]);
  const [costLayersLoading, setCostLayersLoading] = useState(false);
  const [costLayersError, setCostLayersError] = useState<string | null>(null);

  // Stock movements state
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsPage, setMovementsPage] = useState(1);
  const [movementsTotalPages, setMovementsTotalPages] = useState(1);
  const [movementsTotalItems, setMovementsTotalItems] = useState(0);
  const movementsPageSize = 20;

  // Detail modals state
  const [poDetailOpen, setPoDetailOpen] = useState(false);
  const [poDetail, setPoDetail] = useState<PurchaseOrder | null>(null);
  const [poDetailLoading, setPoDetailLoading] = useState(false);

  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false);
  const [receiptDetail, setReceiptDetail] = useState<MaterialReceipt | null>(null);
  const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);

  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState<SalesOrder | null>(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  // Pending orders & recalculate state
  const [pendingOrdersOpen, setPendingOrdersOpen] = useState(false);
  const [pendingOrders, setPendingOrders] = useState<PendingOrderInfo[]>([]);
  const [pendingOrdersLoading, setPendingOrdersLoading] = useState(false);
  const [pendingTotalReserved, setPendingTotalReserved] = useState<number>(0);
  const [pendingOrdersLoaded, setPendingOrdersLoaded] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [pendingOrdersModalOpen, setPendingOrdersModalOpen] = useState(false);

  // Classificações fiscais
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [productOrigins, setProductOrigins] = useState<ProductOrigin[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  useEffect(() => {
    const loadClassifications = async () => {
      try {
        const [types, origins] = await Promise.all([
          productClassificationService.getProductTypes(),
          productClassificationService.getProductOrigins(),
        ]);
        setProductTypes(types);
        setProductOrigins(origins);
      } catch (err) {
        console.error('Erro ao carregar classificações fiscais:', err);
      }
    };
    loadClassifications();
  }, []);

  useEffect(() => {
    productCategoryService
      .listCategories(1, 200, undefined, true)
      .then(({ categories }) => setCategories(categories))
      .catch(() => { /* categories are optional for display */ });
  }, []);

  const categoryName = (categoryId?: number | null): string => {
    if (!categoryId) return '—';
    return categories.find((c) => c.id === categoryId)?.description ?? '—';
  };

  useEffect(() => {
    if (id) {
      loadProduct(id);
    }
  }, [id]);

  const loadProduct = async (productId: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 DETAIL VIEW DEBUG - Loading product ID:', productId);
      const productData = await productService.getProductById(parseInt(productId));
      console.log('🔍 DETAIL VIEW DEBUG - Received product data:', productData);
      console.log('🔍 DETAIL VIEW DEBUG - Product keys:', Object.keys(productData || {}));
      console.log('🔍 DETAIL VIEW DEBUG - stock_quantity:', productData?.stock_quantity);
      console.log('🔍 DETAIL VIEW DEBUG - stock_quantity_available:', productData?.stock_quantity_available);
      
      setProduct(productData);
      console.log('🔍 DETAIL VIEW DEBUG - Product state set, loading will be false');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  };

  // Load cost layers
  const loadCostLayers = useCallback(async () => {
    if (!id) return;
    try {
      setCostLayersLoading(true);
      setCostLayersError(null);
      const data = await productService.getCostLayers(parseInt(id));
      setCostLayers(data.cost_layers || []);
    } catch (err) {
      setCostLayersError(err instanceof Error ? err.message : 'Erro ao carregar camadas de custo');
    } finally {
      setCostLayersLoading(false);
    }
  }, [id]);

  // Load stock movements
  const loadMovements = useCallback(async () => {
    if (!id) return;
    try {
      setMovementsLoading(true);
      const result = await stockMovementService.getMovementsByProduct(
        parseInt(id),
        { page: movementsPage, pageSize: movementsPageSize }
      );
      setMovements(result.data || []);
      setMovementsTotalPages(result.pagination?.totalPages || 1);
      setMovementsTotalItems(result.pagination?.total || 0);
    } catch (err) {
      console.error('Erro ao carregar lançamentos:', err);
    } finally {
      setMovementsLoading(false);
    }
  }, [id, movementsPage]);

  // Tab change handler — always refresh on tab entry to reflect latest data
  const handleTabChange = useCallback((tabId: string) => {
    if (tabId === 'costs') {
      loadCostLayers();
    }
    if (tabId === 'stock') {
      loadMovements();
      // Silently refresh product stock fields without triggering the full-page loading skeleton
      if (id) {
        productService.getProductById(parseInt(id))
          .then(updated => setProduct(updated))
          .catch(() => { /* non-critical — stale data stays visible */ });
      }
    }
  }, [loadCostLayers, loadMovements, id]);

  // Reload movements on page change
  useEffect(() => {
    if (movementsPage > 1) loadMovements();
  }, [movementsPage, loadMovements]);

  // ─── Detail modal handlers ─────────────────────────────────────

  const openPurchaseOrderDetail = async (poId: number) => {
    setPoDetailOpen(true);
    setPoDetailLoading(true);
    try {
      const data = await purchaseOrderService.getPurchaseOrderById(poId);
      setPoDetail(data);
    } catch (err) {
      console.error('Erro ao carregar pedido de compra:', err);
    } finally {
      setPoDetailLoading(false);
    }
  };

  const openMaterialReceiptDetail = async (receiptId: number) => {
    setReceiptDetailOpen(true);
    setReceiptDetailLoading(true);
    try {
      const data = await materialReceiptService.getMaterialReceiptById(receiptId);
      setReceiptDetail(data);
    } catch (err) {
      console.error('Erro ao carregar recebimento de material:', err);
    } finally {
      setReceiptDetailLoading(false);
    }
  };

  const openSalesOrderDetail = async (orderId: number) => {
    setOrderDetailOpen(true);
    setOrderDetailLoading(true);
    try {
      const data = await salesOrderService.getOrderById(orderId);
      setOrderDetail(data);
    } catch (err) {
      console.error('Erro ao carregar pedido de venda:', err);
    } finally {
      setOrderDetailLoading(false);
    }
  };

  // Load pending orders for the current product
  const loadPendingOrders = async () => {
    if (!id) return;
    try {
      setPendingOrdersLoading(true);
      const data = await productService.getPendingOrders(parseInt(id));
      setPendingOrders(data.pending_orders || []);
      setPendingTotalReserved(data.total_reserved || 0);
      setPendingOrdersLoaded(true);
    } catch (err) {
      console.error('Erro ao carregar pedidos pendentes:', err);
    } finally {
      setPendingOrdersLoading(false);
    }
  };

  // Toggle pending orders panel — load on first expand
  const togglePendingOrders = () => {
    const willOpen = !pendingOrdersOpen;
    setPendingOrdersOpen(willOpen);
    if (willOpen && !pendingOrdersLoaded) {
      loadPendingOrders();
    }
  };

  // Recalculate stock for this product
  const handleRecalculateStock = async () => {
    if (!id) return;
    try {
      setRecalculating(true);
      const data = await productService.recalculateStock(parseInt(id));
      // Update product state with new values
      setProduct(prev => prev ? {
        ...prev,
        stock_quantity: data.stock_quantity,
        stock_quantity_available: data.new_available,
      } : prev);
      setPendingTotalReserved(data.total_reserved);
      if (data.previous_available !== data.new_available) {
        alert(`Estoque disponível recalculado: ${data.previous_available} → ${data.new_available}`);
      }
      // Refresh pending orders if panel is open
      if (pendingOrdersOpen) {
        loadPendingOrders();
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao recalcular estoque');
    } finally {
      setRecalculating(false);
    }
  };

  const renderOriginLinks = (mov: StockMovement) => {
    const links: React.ReactNode[] = [];

    if (mov.origin_type === OriginType.RecebimentoMaterial) {
      if (mov.material_receipt_id) {
        links.push(
          <button
            key="receipt"
            className="text-indigo-600 hover:text-indigo-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
            onClick={() => openMaterialReceiptDetail(mov.material_receipt_id!)}
          >
            <ExternalLink size={12} /> Receb. #{mov.material_receipt_id}
          </button>
        );
      }
      if (mov.purchase_order_id) {
        links.push(
          <button
            key="po"
            className="text-purple-600 hover:text-purple-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
            onClick={() => openPurchaseOrderDetail(mov.purchase_order_id!)}
          >
            <ExternalLink size={12} /> PC #{mov.purchase_order_number || mov.purchase_order_id}
          </button>
        );
      }
      if (links.length === 0 && mov.material_receipt_item_id) {
        links.push(<span key="item" className="text-xs text-slate-500">Item #{mov.material_receipt_item_id}</span>);
      }
    }

    if (mov.origin_type === OriginType.PedidoVenda && mov.order_id) {
      links.push(
        <button
          key="order"
          className="text-orange-600 hover:text-orange-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => openSalesOrderDetail(mov.order_id!)}
        >
          <ExternalLink size={12} /> Pedido #{mov.order_id}
        </button>
      );
    }

    if (mov.origin_type === OriginType.PedidoCompra && mov.purchase_order_id) {
      links.push(
        <button
          key="po"
          className="text-purple-600 hover:text-purple-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => openPurchaseOrderDetail(mov.purchase_order_id!)}
        >
          <ExternalLink size={12} /> PC #{mov.purchase_order_number || mov.purchase_order_id}
        </button>
      );
    }

    if (links.length === 0) return <span className="text-xs text-slate-400">—</span>;
    return <div className="flex flex-col gap-1">{links}</div>;
  };

  const handleGenerateLabels = async (quantity: number) => {
    if (!product?.id) return;
    try {
      setLabelLoading(true);
      setLabelError(null);
      await productService.downloadLabels(product.id, quantity);
      setLabelDialogOpen(false);
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : 'Erro ao gerar etiquetas');
    } finally {
      setLabelLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        
        <div className="grid gap-6">
          <Card>
            <CardContent>
              <Skeleton className="h-40" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">Produto não encontrado</h1>
          </div>
        </div>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => id && loadProduct(id)} variant="outline">
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  console.log('🔍 DETAIL VIEW DEBUG - Before product check - product:', product);
  console.log('🔍 DETAIL VIEW DEBUG - Before product check - typeof product:', typeof product);
  console.log('🔍 DETAIL VIEW DEBUG - Before product check - !!product:', !!product);

  if (!product) {
    console.log('🔍 DETAIL VIEW DEBUG - Product is null/undefined, not rendering content');
    return null;
  }

  console.log('🔍 DETAIL VIEW DEBUG - About to render with product:', product);

  return (
    <div className="space-y-6">

      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-h1">{product.description || 'Produto sem nome'}</h1>
            <p className="text-slate-600">SKU: {product.sku || 'N/A'}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <PermissionGuard permission="products.read">
            <Button
              variant="outline"
              icon={<Tag size={20} />}
              onClick={() => setLabelDialogOpen(true)}
            >
              Gerar Etiquetas
            </Button>
          </PermissionGuard>

          <PermissionGuard permission="products.write">
            <Button
              variant="outline"
              icon={<Edit size={20} />}
              onClick={() => navigate(`/products/${product.id}/edit`)}
            >
              Editar
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {labelError && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{labelError}</p>
          </CardContent>
        </Card>
      )}


      <LabelQuantityDialog
        isOpen={labelDialogOpen}
        onClose={() => setLabelDialogOpen(false)}
        onConfirm={handleGenerateLabels}
        loading={labelLoading}
        productName={product.description || product.sku}
      />

      {/* Abas */}
      <Tabs
        tabs={buildTabs()}
        defaultTab="general"
        onTabChange={handleTabChange}
      />

      {/* ─── Purchase Order Detail Modal ───────────────────────── */}
      <Modal isOpen={poDetailOpen} onClose={() => { setPoDetailOpen(false); setPoDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setPoDetailOpen(false); setPoDetail(null); }}
          >
            <X size={16} />
          </button>
          {poDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando pedido de compra...</span>
            </div>
          )}
          {!poDetailLoading && poDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Pedido de Compra #{poDetail.order_number || poDetail.purchase_order_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Fornecedor</span>
                  <span className="font-medium">{poDetail.provider_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data</span>
                  <span className="font-medium">{poDetail.order_date ? new Date(poDetail.order_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className="font-medium">
                    {poDetail.status === 0 ? 'Em Aberto' : poDetail.status === 1 ? 'Atendido' : poDetail.status === 2 ? 'Cancelado' : 'Em Andamento'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Total</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(poDetail.total ?? 0)}</span>
                </div>
              </div>
              {poDetail.items && poDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({poDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-left">SKU</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poDetail.items.map((item, idx) => (
                          <tr key={item.purchase_order_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description}</td>
                            <td className="py-2 px-3 text-slate-500">{item.sku || '—'}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_value)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Material Receipt Detail Modal ────────────────────── */}
      <Modal isOpen={receiptDetailOpen} onClose={() => { setReceiptDetailOpen(false); setReceiptDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setReceiptDetailOpen(false); setReceiptDetail(null); }}
          >
            <X size={16} />
          </button>
          {receiptDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando recebimento...</span>
            </div>
          )}
          {!receiptDetailLoading && receiptDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Recebimento de Material #{receiptDetail.material_receipt_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Fornecedor</span>
                  <span className="font-medium">{receiptDetail.provider_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data Recebimento</span>
                  <span className="font-medium">{receiptDetail.receipt_date ? new Date(receiptDetail.receipt_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    receiptDetail.status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {receiptDetail.status === 1 ? 'Conferido' : 'Em conferência'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Transportadora</span>
                  <span className="font-medium">{receiptDetail.carrier || '—'}</span>
                </div>
              </div>
              {receiptDetail.items && receiptDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({receiptDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                          <th className="py-2 px-3 text-left">Pedido Compra</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receiptDetail.items.map((item, idx) => (
                          <tr key={item.material_receipt_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_value)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency(item.unit_value * item.quantity)}</td>
                            <td className="py-2 px-3 text-slate-500">
                              {item.purchase_order_number
                                ? `#${item.purchase_order_number}`
                                : item.purchase_order_id
                                  ? `PC #${item.purchase_order_id}`
                                  : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ─── Sales Order Detail Modal ─────────────────────────── */}
      <Modal isOpen={orderDetailOpen} onClose={() => { setOrderDetailOpen(false); setOrderDetail(null); }} size="lg">
        <div className="relative">
          <button
            className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600"
            onClick={() => { setOrderDetailOpen(false); setOrderDetail(null); }}
          >
            <X size={16} />
          </button>
          {orderDetailLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-slate-400" />
              <span className="ml-2 text-sm text-slate-500">Carregando pedido de venda...</span>
            </div>
          )}
          {!orderDetailLoading && orderDetail && (
            <div className="space-y-4 p-2">
              <h2 className="text-lg font-bold text-slate-900">
                Pedido de Venda #{orderDetail.order_id}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <span className="text-xs text-slate-500 block">Cliente</span>
                  <span className="font-medium">{orderDetail.customer_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Data</span>
                  <span className="font-medium">{orderDetail.order_date ? new Date(orderDetail.order_date + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Status</span>
                  <span className="font-medium">{orderDetail.order_status || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Total</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(orderDetail.order_sell_total ?? 0)}</span>
                </div>
              </div>
              {orderDetail.items && orderDetail.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Itens ({orderDetail.items.length})</h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="py-2 px-3 text-left">Descrição</th>
                          <th className="py-2 px-3 text-left">SKU</th>
                          <th className="py-2 px-3 text-right">Qtde</th>
                          <th className="py-2 px-3 text-right">Valor Unit.</th>
                          <th className="py-2 px-3 text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetail.items.map((item: any, idx: number) => (
                          <tr key={item.order_item_id || idx} className="border-b border-slate-100">
                            <td className="py-2 px-3">{item.description || '—'}</td>
                            <td className="py-2 px-3 text-slate-500">{item.sku || '—'}</td>
                            <td className="py-2 px-3 text-right">{item.quantity}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(item.unit_price)}</td>
                            <td className="py-2 px-3 text-right font-medium">{formatCurrency((item.unit_price || 0) * (item.quantity || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );

  function buildTabs(): TabItem[] {
    return [
      {
        id: 'general',
        label: 'Informações Gerais',
        icon: <Package size={16} />,
        content: renderGeneralTab(),
      },
      {
        id: 'costs',
        label: 'Custos FIFO',
        icon: <Layers size={16} />,
        content: renderCostsTab(),
      },
      {
        id: 'stock',
        label: 'Controle de Estoque',
        icon: <ClipboardList size={16} />,
        content: renderStockTab(),
      },
    ];
  }

  function renderGeneralTab() {
    if (!product) return null;

    const productTypeLabel = productTypes.find(
      (t) => t.product_type_id === product.product_type_id
    )?.product_type_description || (product.product_type_id ? `ID ${product.product_type_id}` : '-');

    const productOriginLabel = productOrigins.find(
      (o) => o.product_origin_id === product.product_origin_id
    )?.product_origin_description || (product.product_origin_id ? `ID ${product.product_origin_id}` : '-');

    return (
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-3 items-start">
        {/* Informações Básicas */}
        <Card>
          <CardHeader title="Informações Básicas" />
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">SKU</p>
                <p className="font-medium">{product.sku || '-'}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Descrição</p>
                <p className="font-medium leading-relaxed">{product.description || '-'}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Categoria</p>
                <p className="font-medium">{categoryName(product.category_id)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dados Fiscais */}
        <Card>
          <CardHeader title="Dados Fiscais" />
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">NCM</p>
                <p className="font-medium font-mono">{product.ncm || '-'}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Tipo do Produto (SPED)</p>
                <p className="font-medium">{productTypeLabel}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-small text-slate-500 mb-1">Origem do Produto</p>
                <p className="font-medium">{productOriginLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preços e Custos */}
        <Card>
          <CardHeader title="Preços e Custos" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="text-green-600" size={20} />
                <div>
                  <p className="text-small text-slate-500">Preço de Venda</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(product.price || 0)}
                  </p>
                </div>
              </div>
              {product.cost && (
                <div className="flex items-center gap-3">
                  <DollarSign className="text-slate-400" size={20} />
                  <div>
                    <p className="text-small text-slate-500">Custo</p>
                    <p className="text-xl font-medium">{formatCurrency(product.cost)}</p>
                  </div>
                </div>
              )}
            </div>
            {product.price && product.cost && (
              <div className="mt-6 pt-4 border-t border-slate-200 grid grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-small text-slate-500">Margem de Lucro</span>
                  <span className="font-medium text-green-600">
                    {((product.price - product.cost) / product.price * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-small text-slate-500">Lucro</span>
                  <span className="font-medium">
                    {formatCurrency(product.price - product.cost)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dimensões e Peso */}
        <Card>
          <CardHeader title="Dimensões e Peso" />
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">Largura (cm)</p>
                <p className="font-medium">{product.width || 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Altura (cm)</p>
                <p className="font-medium">{product.height || 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Comprimento (cm)</p>
                <p className="font-medium">{product.length || 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Peso (kg)</p>
                <p className="font-medium">{product.total_weight || 0}</p>
              </div>
            </div>
            {(product.width || product.height || product.length) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex justify-between items-center">
                  <span className="text-small text-slate-500">Volume Calculado</span>
                  <span className="font-medium">
                    {((product.width || 0) * (product.height || 0) * (product.length || 0) / 1000).toFixed(3)} L
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configurações de Envio */}
        <Card>
          <CardHeader title="Configurações de Envio" />
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Truck className="text-slate-400" size={20} />
                  <span className="text-slate-700">Frete grátis</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.free_shipping ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {product.free_shipping ? 'Sim' : 'Não'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="text-slate-400" size={20} />
                  <span className="text-slate-700">Outros produtos dentro dele</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.can_ship_with_another_inside ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {product.can_ship_with_another_inside ? 'Permitido' : 'Não permitido'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="text-slate-400" size={20} />
                  <span className="text-slate-700">Produto frágil</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.is_fragile ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                  {product.is_fragile ? 'Sim' : 'Não'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="text-slate-400" size={20} />
                  <span className="text-slate-700">Status</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {product.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Tag className="text-slate-400" size={20} />
                  <span className="text-slate-700">Visível no CRM</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.is_visible_on_crm !== false ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                  {product.is_visible_on_crm !== false ? 'Sim' : 'Não'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="text-slate-400" size={20} />
                  <span className="text-slate-700">Fabricação própria</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.is_manufactured === true ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-600'}`}>
                  {product.is_manufactured === true ? 'Sim' : 'Não'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="text-slate-400" size={20} />
                  <span className="text-slate-700">Produto personalizado</span>
                </div>
                <span className={`px-2 py-1 rounded text-sm ${product.is_custom_product === true ? 'bg-pink-100 text-pink-800' : 'bg-slate-100 text-slate-600'}`}>
                  {product.is_custom_product === true ? 'Sim' : 'Não'}
                </span>
              </div>
            </div>

            {/* Foto do projeto aprovado (blob privado — carregada via proxy) */}
            {product.is_custom_product === true && product.has_project_photo === true && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-700 mb-2">Foto do Projeto</p>
                <ProjectPhotoThumb productId={product.id} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Informações de Sistema */}
        <Card>
          <CardHeader title="Informações de Sistema" />
          <CardContent>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Criação</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-slate-400" size={16} />
                    <span className="text-sm text-slate-600">
                      {product.created_at ? formatDate(product.created_at) : 'N/A'}
                    </span>
                  </div>
                  {product.created_by && (
                    <div className="flex items-center gap-2">
                      <User className="text-slate-400" size={16} />
                      <span className="text-sm text-slate-600">{product.created_by}</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Última Modificação</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="text-slate-400" size={16} />
                    <span className="text-sm text-slate-600">
                      {product.updated_at ? formatDate(product.updated_at) : 'N/A'}
                    </span>
                  </div>
                  {product.last_updated_by && (
                    <div className="flex items-center gap-2">
                      <User className="text-slate-400" size={16} />
                      <span className="text-sm text-slate-600">{product.last_updated_by}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  function renderCostsTab() {
    if (!product) return null;

    return (
      <div className="grid gap-6">
        {/* Overview de custos */}
        <Card>
          <CardHeader title="Resumo de Custos" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="text-green-600" size={20} />
                <div>
                  <p className="text-small text-slate-500">Preço de Venda</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(product.price || 0)}
                  </p>
                </div>
              </div>
              {product.cost != null && (
                <div className="flex items-center gap-3">
                  <DollarSign className="text-slate-400" size={20} />
                  <div>
                    <p className="text-small text-slate-500">Custo Atual</p>
                    <p className="text-xl font-medium">{formatCurrency(product.cost)}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Camadas de custo FIFO */}
        <Card>
          <CardHeader title="Camadas de Custo FIFO (Ativas)" />
          <CardContent className="p-0">
            {costLayersLoading && (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <span className="text-sm text-slate-500 mt-2 block">Carregando camadas de custo...</span>
              </div>
            )}
            {costLayersError && (
              <div className="p-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  {costLayersError}
                </div>
              </div>
            )}
            {!costLayersLoading && !costLayersError && costLayers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-5xl text-slate-200 mb-3">📦</div>
                <h3 className="text-base font-medium text-slate-700 mb-1">Nenhuma camada de custo ativa</h3>
                <p className="text-sm text-slate-500">Este produto ainda não possui camadas de custo FIFO.</p>
              </div>
            )}
            {!costLayersLoading && costLayers.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <tr>
                      <th className="text-left">ID</th>
                      <th className="text-left">Fornecedor</th>
                      <th className="text-left">Pedido Compra</th>
                      <th className="text-left">Recebimento</th>
                      <th className="text-right">Custo Unit.</th>
                      <th className="text-right">Qtde Original</th>
                      <th className="text-right">Qtde Restante</th>
                      <th className="text-left">Data Entrada</th>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {costLayers.map((layer) => (
                      <TableRow key={layer.stock_cost_layer_id}>
                        <TableCell>{layer.stock_cost_layer_id}</TableCell>
                        <TableCell>{layer.provider_name || '—'}</TableCell>
                        <TableCell>
                          {layer.purchase_order_id ? (
                            <button
                              className="text-purple-600 hover:text-purple-800 hover:underline text-sm font-medium inline-flex items-center gap-1"
                              onClick={() => openPurchaseOrderDetail(layer.purchase_order_id!)}
                            >
                              <ExternalLink size={12} /> #{layer.purchase_order_number || layer.purchase_order_id}
                            </button>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {layer.material_receipt_id ? (
                            <button
                              className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm font-medium inline-flex items-center gap-1"
                              onClick={() => openMaterialReceiptDetail(layer.material_receipt_id!)}
                            >
                              <ExternalLink size={12} /> #{layer.material_receipt_id}
                            </button>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(layer.unit_cost)}
                        </TableCell>
                        <TableCell className="text-right">
                          {layer.original_quantity.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                        </TableCell>
                        <TableCell className="text-right">
                          {layer.remaining_quantity.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{formatDate(layer.entry_date)}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300">
                      <td colSpan={4} className="py-2 px-4 text-right font-semibold text-slate-700">Totais:</td>
                      <td className="py-2 px-4 text-right font-bold text-slate-700">
                        {formatCurrency(
                          costLayers.reduce((sum, l) => sum + l.remaining_quantity * l.unit_cost, 0) /
                          (costLayers.reduce((sum, l) => sum + l.remaining_quantity, 0) || 1)
                        )}
                        <span className="text-xs font-normal text-slate-400 block">Custo médio</span>
                      </td>
                      <td />
                      <td className="py-2 px-4 text-right font-bold text-emerald-700">
                        {costLayers.reduce((sum, l) => sum + l.remaining_quantity, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  function renderStockTab() {
    if (!product) return null;

    const MovementTypeBadge: React.FC<{ type: number }> = ({ type }) => {
      const config: Record<number, { label: string; className: string; icon: React.ReactNode }> = {
        [MovementType.Entrada]: { label: 'Entrada', className: 'bg-green-100 text-green-700 border-green-200', icon: <ArrowDownCircle size={14} /> },
        [MovementType.Saida]: { label: 'Saída', className: 'bg-red-100 text-red-700 border-red-200', icon: <ArrowUpCircle size={14} /> },
        [MovementType.Saldo]: { label: 'Saldo', className: 'bg-blue-100 text-blue-700 border-blue-200', icon: <MinusCircle size={14} /> },
      };
      const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600', icon: null };
      return (
        <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
          {c.icon} {c.label}
        </span>
      );
    };

    const OriginTypeBadge: React.FC<{ type: number }> = ({ type }) => {
      const config: Record<number, { label: string; className: string }> = {
        [OriginType.PedidoCompra]: { label: 'Pedido de Compra', className: 'bg-purple-100 text-purple-700 border-purple-200' },
        [OriginType.PedidoVenda]: { label: 'Pedido de Venda', className: 'bg-orange-100 text-orange-700 border-orange-200' },
        [OriginType.AcertoManual]: { label: 'Acerto Manual', className: 'bg-amber-100 text-amber-700 border-amber-200' },
        [OriginType.Balanco]: { label: 'Balanço', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
        [OriginType.RecebimentoMaterial]: { label: 'Recebimento Material', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
      };
      const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600' };
      return (
        <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
          {c.label}
        </span>
      );
    };

    const formatQty = (value?: number | null) => {
      if (value === undefined || value === null) return '—';
      return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    };

    const formatMovDate = (dateStr: string) =>
      new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });

    // Color helper for stock_quantity_available
    const getAvailableColor = () => {
      const available = product.stock_quantity_available;
      const minStock = product.min_stock_quantity || 0;
      if (available === null || available === undefined) return 'text-slate-500';
      if (available < 0) return 'text-red-600';
      if (available === 0) return 'text-amber-600';
      if (minStock > 0 && available < minStock) return 'text-orange-600';
      return 'text-emerald-600';
    };

    const getAvailableBgColor = () => {
      const available = product.stock_quantity_available;
      const minStock = product.min_stock_quantity || 0;
      if (available === null || available === undefined) return 'bg-slate-100 text-slate-500';
      if (available < 0) return 'bg-red-100 text-red-800';
      if (available === 0) return 'bg-amber-100 text-amber-800';
      if (minStock > 0 && available < minStock) return 'bg-orange-100 text-orange-800';
      return 'bg-emerald-100 text-emerald-800';
    };

    const getAvailableLabel = () => {
      const available = product.stock_quantity_available;
      const minStock = product.min_stock_quantity || 0;
      if (available === null || available === undefined) return 'Indisponível';
      if (available < 0) return 'Overselling';
      if (available === 0) return 'Sem Estoque';
      if (minStock > 0 && available < minStock) return 'Estoque Baixo';
      return 'Disponível';
    };

    const reserved = (product.stock_quantity != null && product.stock_quantity_available != null)
      ? (product.stock_quantity - product.stock_quantity_available)
      : null;

    return (
      <div className="grid gap-6">
        {/* Resumo de estoque */}
        <Card>
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Saldo de Estoque</h2>
            <PermissionGuard permission="products.recalculate_stock">
              <Button
                variant="outline"
                size="sm"
                icon={<RefreshCw size={16} className={recalculating ? 'animate-spin' : ''} />}
                onClick={handleRecalculateStock}
                disabled={recalculating}
              >
                Recalcular estoque disponível
              </Button>
            </PermissionGuard>
          </div>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Físico</p>
                <p className="text-2xl font-bold text-slate-900">{product.stock_quantity ?? 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Disponível</p>
                <div className="flex items-center gap-2">
                  <p className={clsx('text-2xl font-bold', getAvailableColor())}>
                    {product.stock_quantity_available != null ? (
                      <>
                        {product.stock_quantity_available < 0 && <AlertTriangle size={18} className="inline mr-1" />}
                        {product.stock_quantity_available}
                      </>
                    ) : '—'}
                  </p>
                  <span className={clsx('text-sm px-2 py-1 rounded', getAvailableBgColor())}>
                    {getAvailableLabel()}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Reservado em Pedidos</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl font-bold text-amber-600">
                    {reserved != null ? reserved : '—'}
                  </p>
                  {reserved != null && reserved > 0 && (
                    <button
                      className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                      onClick={() => setPendingOrdersModalOpen(true)}
                    >
                      Ver pedidos
                    </button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Mínimo</p>
                <p className="font-medium">{product.min_stock_quantity ?? 0}</p>
              </div>
              <div>
                <p className="text-small text-slate-500 mb-1">Estoque Máximo</p>
                <p className="font-medium">{product.max_stock_quantity ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pedidos Pendentes (painel expansível) */}
        <Card>
          <button
            type="button"
            className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
            onClick={togglePendingOrders}
          >
            <div className="flex items-center gap-2">
              <Package size={20} className="text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Pedidos Pendentes
                {(pendingOrdersLoaded || reserved != null) && (
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    ({pendingOrdersLoaded ? pendingTotalReserved : (reserved ?? 0)} unid. reservadas)
                  </span>
                )}
              </h2>
            </div>
            <ChevronDown
              size={20}
              className={clsx('text-slate-400 transition-transform', pendingOrdersOpen && 'rotate-180')}
            />
          </button>
          {pendingOrdersOpen && (
            <CardContent className="p-0 border-t border-slate-200">
              {pendingOrdersLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <span className="text-sm text-slate-500 mt-2 block">Carregando pedidos pendentes...</span>
                </div>
              ) : pendingOrders.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-500">Nenhum pedido pendente reservando este produto</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <tr>
                        <th className="text-left">Pedido</th>
                        <th className="text-left">Data</th>
                        <th className="text-left">Status</th>
                        <th className="text-left">Cliente</th>
                        <th className="text-right">Qtd Reservada</th>
                      </tr>
                    </TableHeader>
                    <tbody>
                      {pendingOrders.map((po) => (
                        <TableRow key={po.order_id}>
                          <TableCell>
                            <button
                              className="text-emerald-600 hover:text-emerald-800 hover:underline text-sm font-medium inline-flex items-center gap-1"
                              onClick={() => navigate(`/sales-orders/${po.order_id}`)}
                            >
                              <ExternalLink size={12} /> #{po.order_id}
                            </button>
                          </TableCell>
                          <TableCell>
                            {po.order_date
                              ? new Date(po.order_date).toLocaleDateString('pt-BR')
                              : '—'}
                          </TableCell>
                          <TableCell>{po.order_status || '—'}</TableCell>
                          <TableCell>{po.customer_name || '—'}</TableCell>
                          <TableCell className="text-right font-medium">{po.quantity_reserved}</TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Tabela de movimentações */}
        <Card>
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <ClipboardList size={20} className="text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Lançamentos de Estoque</h2>
              <span className="text-sm text-slate-500">({movementsTotalItems} registros)</span>
            </div>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">Data</th>
                    <th className="text-left">Origem</th>
                    <th className="text-left">Referência</th>
                    <th className="text-center">Tipo</th>
                    <th className="text-right">Quantidade</th>
                    <th className="text-right">Saldo Anterior</th>
                    <th className="text-right">Novo Saldo</th>
                    <th className="text-right">Preço Compra</th>
                    <th className="text-right">Preço Custo</th>
                    <th className="text-left">Observação</th>
                    <th className="text-left">Usuário</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {movementsLoading && (
                    <tr>
                      <td colSpan={11} className="text-center py-12">
                        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        <span className="text-sm text-slate-500 mt-2 block">Carregando lançamentos...</span>
                      </td>
                    </tr>
                  )}
                  {!movementsLoading && movements.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-12">
                        <div className="text-5xl text-slate-200 mb-3">📦</div>
                        <h3 className="text-base font-medium text-slate-700 mb-1">Nenhum lançamento encontrado</h3>
                        <p className="text-sm text-slate-500">Não há lançamentos de estoque registrados para este produto</p>
                      </td>
                    </tr>
                  )}
                  {!movementsLoading && movements.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell className="whitespace-nowrap">{formatMovDate(mov.movement_date)}</TableCell>
                      <TableCell><OriginTypeBadge type={mov.origin_type} /></TableCell>
                      <TableCell>{renderOriginLinks(mov)}</TableCell>
                      <TableCell className="text-center"><MovementTypeBadge type={mov.movement_type} /></TableCell>
                      <TableCell className="text-right font-medium">{formatQty(mov.quantity)}</TableCell>
                      <TableCell className="text-right">{formatQty(mov.previous_balance)}</TableCell>
                      <TableCell className="text-right">{formatQty(mov.new_balance)}</TableCell>
                      <TableCell className="text-right">{mov.purchase_price != null ? formatCurrency(mov.purchase_price) : '—'}</TableCell>
                      <TableCell className="text-right">{mov.cost_price != null ? formatCurrency(mov.cost_price) : '—'}</TableCell>
                      <TableCell className="max-w-xs truncate">{mov.notes || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">{mov.created_by}</TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
            {movementsTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Página {movementsPage} de {movementsTotalPages} ({movementsTotalItems} registros)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMovementsPage(p => Math.max(1, p - 1))}
                    disabled={movementsPage === 1}
                    icon={<ChevronLeft size={16} />}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMovementsPage(p => Math.min(movementsTotalPages, p + 1))}
                    disabled={movementsPage === movementsTotalPages}
                    icon={<ChevronRight size={16} />}
                    iconPosition="right"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Orders Modal */}
        <PendingOrdersModal
          isOpen={pendingOrdersModalOpen}
          onClose={() => setPendingOrdersModalOpen(false)}
          productId={product.id}
          productSku={product.sku}
          productDescription={product.description || undefined}
        />
      </div>
    );
  }
};
